import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ConnectCapabilitiesCard } from "@/domains/settings/features/integrations/google-business/components/connect-capabilities-card";
import { ConnectGoogleBusinessDialog } from "@/domains/settings/features/integrations/google-business/components/connect-google-business-dialog";
import { LocationPickerDialog } from "@/domains/settings/features/integrations/google-business/components/location-picker-dialog";
import { PushSyncToggleCard } from "@/domains/settings/features/integrations/google-business/components/push-sync-toggle-card";
import { ReauthBanner } from "@/domains/settings/features/integrations/google-business/components/reauth-banner";
import { SalonMismatchBanner } from "@/domains/settings/features/integrations/google-business/components/salon-mismatch-banner";
import { SyncToggleCard } from "@/domains/settings/features/integrations/google-business/components/sync-toggle-card";
import { useGoogleBusinessConnection } from "@/domains/settings/features/integrations/google-business/hooks/use-google-business-connection";
import { useGoogleBusinessPending } from "@/domains/settings/features/integrations/google-business/hooks/use-google-business-pending";
import { useGoogleConnectionHealth } from "@/domains/settings/features/integrations/google-business/hooks/use-google-connection-health";
import { useNotifyPendingInvitation } from "@/domains/settings/features/integrations/google-business/hooks/use-notify-pending-invitation";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BaseNavigationBar, SettingsBackButton } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { flattenSalonsWithFranchise } from "@/lib/components/salon-selector/utils";
import { useSalonStore } from "@/lib/store/salon";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { GoogleConnectionSheet } from "./components/google-connection-sheet";
import { GoogleConnectionStrip } from "./components/google-connection-strip";

const ERROR_TO_MESSAGE: Record<string, string> = {
  access_denied: "Connection cancelled.",
  invalid_callback: "We could not complete the Google connection. Please try again.",
  oauth_failed: "Something went wrong connecting to Google. Please try again.",
  gbp_api_disabled:
    "Google Business APIs aren't enabled in your Google Cloud project yet. Click the link below, enable the API, then reconnect.",
  gbp_account_forbidden:
    "Your Google account doesn't have access to any Business Profile location. Make sure you're using the Google account that owns the salon's GBP listing.",
  gbp_quota_exceeded:
    "Google Business API quota is 0 for this project — the API needs to be approved by Google first. Click the link to apply for access.",
};

/**
 * Google Business settings page — focused only on the connection lifecycle
 * (capabilities pitch, OAuth handoff, location picker, post-connect status
 * strip, reauth banner, disconnect). The reviews list and review-creation
 * UI moved to the top-level /reviews route after the boss split this page;
 * search MEMORY for "reviews top-level" if you're hunting that work.
 */
export function SalonReviewSettingsPage() {
  const { t } = useTranslation("settings");

  // --- Google Business connection state -----------------------------------
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingFlag = searchParams.get("pending") === "1";
  const intendedSalonId = searchParams.get("salonId");
  const errorFlag = searchParams.get("error");

  const { franchiseAccess, currentSalon, setCurrentSalon } = useSalonStore();

  const {
    connection,
    loading: connectionLoading,
    error: connectionError,
    salonId,
    initiateConnect,
    initiating,
    linkLocation,
    linking,
    disconnect,
    disconnecting,
    setSyncEnabled,
    settingSyncEnabled,
    setHoursSyncEnabled,
    settingHoursSyncEnabled,
    setHolidaysSyncEnabled,
    settingHolidaysSyncEnabled,
    setServicesSyncEnabled,
    settingServicesSyncEnabled,
    syncHoursNow,
    syncingHours,
    syncHolidaysNow,
    syncingHolidays,
    syncServicesNow,
    syncingServices,
    syncReviewsNow,
    syncingReviews,
  } = useGoogleBusinessConnection();

  const hasSalonMismatch = Boolean(
    pendingFlag && intendedSalonId && salonId && intendedSalonId !== salonId
  );

  const franchiseRole = currentSalon
    ? useSalonStore.getState().getFranchiseRole(currentSalon.franchiseId)
    : null;
  const isAdmin =
    franchiseRole === "ADMIN" ||
    franchiseRole === "SUPER_ADMIN" ||
    franchiseRole === "FRANCHISE_ADMIN";

  const intendedSalon = useMemo(() => {
    if (!intendedSalonId) return null;
    return (
      flattenSalonsWithFranchise(franchiseAccess).find((s) => s.id === intendedSalonId) ?? null
    );
  }, [intendedSalonId, franchiseAccess]);

  const { locations: pendingLocations, loading: pendingLoading } = useGoogleBusinessPending(
    pendingFlag && !hasSalonMismatch
  );

  const {
    defaultManagerEmail,
    notify: notifyPendingInvitation,
    notifying,
  } = useNotifyPendingInvitation();

  const { health } = useGoogleConnectionHealth();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [manageSheetOpen, setManageSheetOpen] = useState(false);
  const autoLinkAttemptedRef = useRef(false);

  // Show toast for OAuth-error redirects, then clear the flag. The
  // gbp_api_disabled / gbp_quota_exceeded cases carry an actionable URL from
  // Google — we surface it as a button in the toast so the operator can fix
  // their Google Cloud setup in one click without having to read backend logs.
  useEffect(() => {
    if (!errorFlag) return;
    const message = ERROR_TO_MESSAGE[errorFlag] ?? "Could not connect to Google.";
    const actionUrl = searchParams.get("activationUrl") ?? searchParams.get("helpUrl");

    const actionLabel =
      errorFlag === "gbp_api_disabled"
        ? "Open Google Cloud"
        : errorFlag === "gbp_quota_exceeded"
          ? "Apply for GBP access"
          : null;

    if (actionUrl && actionLabel) {
      toast.error(message, {
        duration: 20_000,
        action: {
          label: actionLabel,
          onClick: () => window.open(actionUrl, "_blank", "noopener,noreferrer"),
        },
      });
    } else {
      toast.error(message);
    }

    const next = new URLSearchParams(searchParams);
    next.delete("error");
    next.delete("service");
    next.delete("activationUrl");
    next.delete("helpUrl");
    setSearchParams(next, { replace: true });
  }, [errorFlag, searchParams, setSearchParams]);

  // Auto-link when exactly one location is returned, else open the picker
  useEffect(() => {
    if (!pendingFlag || hasSalonMismatch || !pendingLocations) return;
    if (autoLinkAttemptedRef.current) return;

    if (pendingLocations.length === 0) {
      toast.error(
        "This Google account has no business locations. Try connecting with a different account."
      );
      const next = new URLSearchParams(searchParams);
      next.delete("pending");
      next.delete("salonId");
      setSearchParams(next, { replace: true });
      return;
    }

    if (pendingLocations.length === 1) {
      autoLinkAttemptedRef.current = true;
      const only = pendingLocations[0];
      void linkLocation(only.accountId, only.locationId).then((result) => {
        if (result) {
          const next = new URLSearchParams(searchParams);
          next.delete("pending");
          next.delete("salonId");
          setSearchParams(next, { replace: true });
        }
      });
    } else {
      setPickerOpen(true);
    }
  }, [
    pendingFlag,
    hasSalonMismatch,
    pendingLocations,
    linkLocation,
    searchParams,
    setSearchParams,
  ]);

  // If pending=1 but backend says nothing is pending, surface a friendly message
  useEffect(() => {
    if (!pendingFlag || hasSalonMismatch || pendingLoading) return;
    if (pendingLocations === null) {
      toast.error("Connection timed out, please try again.");
      const next = new URLSearchParams(searchParams);
      next.delete("pending");
      next.delete("salonId");
      setSearchParams(next, { replace: true });
    }
  }, [
    pendingFlag,
    hasSalonMismatch,
    pendingLoading,
    pendingLocations,
    searchParams,
    setSearchParams,
  ]);

  const handleStartConnect = async () => {
    const authUrl = await initiateConnect();
    if (authUrl) {
      window.location.href = authUrl;
    }
  };

  const handlePickLocation = async (accountId: string, locationId: string) => {
    const result = await linkLocation(accountId, locationId);
    if (result) {
      setPickerOpen(false);
      const next = new URLSearchParams(searchParams);
      next.delete("pending");
      next.delete("salonId");
      setSearchParams(next, { replace: true });
    }
  };

  const handleDisconnect = async () => {
    // Close the sheet BEFORE awaiting disconnect — once the refetch resolves,
    // `connection` becomes null and the `{connection ? <Sheet/> : null}` wrapper
    // force-unmounts the sheet while Radix still thinks it's open, leaking the
    // body scroll-lock + overlay and producing a blank-looking page.
    setManageSheetOpen(false);
    await disconnect();
  };

  const handleSwitchToIntendedSalon = () => {
    if (!intendedSalon) return;
    setCurrentSalon(intendedSalon);
  };

  const handleCancelMismatch = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("pending");
    next.delete("salonId");
    setSearchParams(next, { replace: true });
  };

  const isPickerVisible =
    pickerOpen && Array.isArray(pendingLocations) && pendingLocations.length > 1;
  const isAutoLinking =
    pendingFlag && !hasSalonMismatch && pendingLocations?.length === 1 && linking;

  // --- Decide which connection section to render --------------------------
  // Order: no-salon → mismatch banner → initial-load → connected strip →
  // auto-linking → capabilities pitch (default for unconnected salons).
  let connectionSection: React.ReactNode = null;
  if (!salonId) {
    connectionSection = (
      <div className="text-sm text-muted-foreground">{t("reviews.selectSalonPrompt")}</div>
    );
  } else if (hasSalonMismatch) {
    connectionSection = (
      <SalonMismatchBanner
        intendedSalonName={intendedSalon?.name ?? null}
        onSwitch={handleSwitchToIntendedSalon}
        onCancel={handleCancelMismatch}
        switchDisabled={!intendedSalon}
      />
    );
  } else if (connectionLoading && !connection) {
    connectionSection = null;
  } else if (connection) {
    connectionSection = (
      <div className="space-y-3">
        {connection.reauthRequired && connection.authMode === "OWNER_OAUTH" ? (
          <ReauthBanner onReconnect={handleStartConnect} isLoading={initiating} />
        ) : null}
        <GoogleConnectionStrip
          locationName={connection.locationName}
          authMode={connection.authMode}
          lastSyncAt={health?.lastSyncAt ?? connection.lastSyncAt ?? null}
          lastSyncError={health?.lastSyncError ?? connection.lastSyncError ?? null}
          reauthRequired={
            (health?.reauthRequired ?? false) ||
            (health?.managerReauthRequired ?? false) ||
            (connection.authMode === "MANAGED_PLATFORM" && health?.managerActive === false) ||
            connection.reauthRequired
          }
          onManage={() => setManageSheetOpen(true)}
        />
        <SyncToggleCard
          syncEnabled={connection.syncEnabled}
          onChange={(enabled) => {
            void setSyncEnabled(enabled);
          }}
          isPending={settingSyncEnabled}
          isAdmin={isAdmin}
          onSyncNow={() => {
            void syncReviewsNow();
          }}
          isSyncing={syncingReviews}
        />
        <PushSyncToggleCard
          pushType="hours"
          i18nKey="syncHoursToGoogle"
          enabled={connection.syncHoursToGoogle}
          onChange={(enabled) => {
            void setHoursSyncEnabled(enabled);
          }}
          isPending={settingHoursSyncEnabled}
          isAdmin={isAdmin}
          onSyncNow={() => {
            void syncHoursNow();
          }}
          isSyncing={syncingHours}
        />
        <PushSyncToggleCard
          pushType="holidays"
          i18nKey="syncHolidaysToGoogle"
          enabled={connection.syncHolidaysToGoogle}
          onChange={(enabled) => {
            void setHolidaysSyncEnabled(enabled);
          }}
          isPending={settingHolidaysSyncEnabled}
          isAdmin={isAdmin}
          onSyncNow={() => {
            void syncHolidaysNow();
          }}
          isSyncing={syncingHolidays}
        />
        <PushSyncToggleCard
          pushType="services"
          i18nKey="syncServicesToGoogle"
          enabled={connection.syncServicesToGoogle}
          onChange={(enabled) => {
            void setServicesSyncEnabled(enabled);
          }}
          isPending={settingServicesSyncEnabled}
          isAdmin={isAdmin}
          onSyncNow={() => {
            void syncServicesNow();
          }}
          isSyncing={syncingServices}
        />
      </div>
    );
  } else if (isAutoLinking) {
    connectionSection = (
      <div className="text-sm text-muted-foreground">Finishing Google setup…</div>
    );
  } else {
    // Unconnected — show the single capabilities card with one CTA that
    // opens the connect dialog. The dialog itself presents OAuth vs the
    // managed-flow disclosure.
    connectionSection = (
      <ConnectCapabilitiesCard
        onConnect={() => setConnectDialogOpen(true)}
        isLoading={initiating}
      />
    );
  }

  return (
    <>
      <BaseNavigationBar
        title={t("reviews.pageTitle")}
        subtitle={t("reviews.pageSubtitle")}
        leadElement={<SettingsBackButton key="back-button" />}
        trailingElements={[<SettingsSearchTrigger key="search-trigger" />]}
      />

      <PageContent>
        <div className="space-y-6">
          <ErrorAlert error={connectionError ?? undefined} />
          {connectionSection}
        </div>
      </PageContent>

      {isPickerVisible ? (
        <LocationPickerDialog
          open={isPickerVisible}
          locations={pendingLocations ?? []}
          isSubmitting={linking}
          onCancel={() => {
            setPickerOpen(false);
            const next = new URLSearchParams(searchParams);
            next.delete("pending");
            next.delete("salonId");
            setSearchParams(next, { replace: true });
          }}
          onSubmit={handlePickLocation}
        />
      ) : null}

      {connection ? (
        <GoogleConnectionSheet
          open={manageSheetOpen}
          onOpenChange={setManageSheetOpen}
          connection={connection}
          health={health}
          onDisconnect={handleDisconnect}
          disconnecting={disconnecting}
          onReconnect={handleStartConnect}
          reconnecting={initiating}
        />
      ) : null}

      <ConnectGoogleBusinessDialog
        open={connectDialogOpen}
        onOpenChange={setConnectDialogOpen}
        onDirectConnect={handleStartConnect}
        initiating={initiating}
        managerEmail={defaultManagerEmail}
        onManagedFlowSubmitted={() => {
          void notifyPendingInvitation();
        }}
        notifying={notifying}
      />
    </>
  );
}
